import { ciede2000 } from '../color/ciede2000';
import { rgbToLab } from '../color/srgb-lab';
import type { RgbColor } from '../color/types';
import type { BeadColor } from '../catalog/types';

/**
 * Recherche linéaire de l'indice de la couleur la plus proche en distance CIEDE2000.
 * Brique partagée par `quantize()` (nearest-neighbor pur) et `dither()` (Floyd-Steinberg).
 *
 * @throws si la palette est vide.
 */
export function findNearestIndex(pixel: RgbColor, palette: ReadonlyArray<BeadColor>): number {
  if (palette.length === 0) {
    throw new Error('Palette vide.');
  }
  const lab = rgbToLab(pixel);
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < palette.length; i++) {
    const candidate = palette[i];
    if (candidate === undefined) continue;
    const d = ciede2000(lab, candidate.lab);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Variante contrainte à un sous-ensemble d'indices de la palette (Lot P2 :
 * réduction du nombre de couleurs — cf. `reduce.ts`). Retourne toujours un
 * indice de la palette COMPLÈTE, jamais un indice du sous-ensemble, pour que
 * `Pattern.paletteColorIds` reste canonique.
 *
 * @throws si `allowed` est vide.
 */
export function findNearestIndexAmong(
  pixel: RgbColor,
  palette: ReadonlyArray<BeadColor>,
  allowed: ReadonlyArray<number>,
): number {
  if (allowed.length === 0) {
    throw new Error('Sous-ensemble de palette vide.');
  }
  const lab = rgbToLab(pixel);
  let bestIdx = allowed[0] ?? 0;
  let bestDist = Infinity;
  for (const i of allowed) {
    const candidate = palette[i];
    if (candidate === undefined) continue;
    const d = ciede2000(lab, candidate.lab);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/** Bits de RGB ignorés dans la clé de cache : godets de 4 unités (2 bits).
 *  Un écart de 4/255 est très sous le seuil perceptuel (ΔE ≪ 1) et les
 *  cellules « plus proche voisin » de la palette font des dizaines d'unités
 *  de large → jamais de banding introduit, mais ~10-50× moins de recherches
 *  CIEDE2000 sur les grands patrons (revue V2 : dither 500 = 26 s → sec.). */
const CACHE_QUANT_BITS = 2;

/**
 * Résolveur « pixel → indice palette » avec mémoïsation par godet RGB.
 *
 * La recherche CIEDE2000 est le coût dominant du pipeline (O(pixels × palette),
 * et la palette est passée à 54 couleurs). Ce résolveur mutualise les
 * recherches : les pixels tombant dans le même cube RGB 4×4×4 partagent le
 * résultat. À créer UNE fois par conversion (le cache est lié à la palette et
 * au sous-ensemble `allowed`).
 */
export function makeNearestResolver(
  palette: ReadonlyArray<BeadColor>,
  allowed?: ReadonlyArray<number>,
): (r: number, g: number, b: number) => number {
  if (palette.length === 0) {
    throw new Error('Palette vide.');
  }
  if (allowed !== undefined && allowed.length === 0) {
    throw new Error('Sous-ensemble de palette vide.');
  }
  const cache = new Map<number, number>();
  const q = CACHE_QUANT_BITS;
  return (r: number, g: number, b: number): number => {
    const ri = r < 0 ? 0 : r > 255 ? 255 : r;
    const gi = g < 0 ? 0 : g > 255 ? 255 : g;
    const bi = b < 0 ? 0 : b > 255 ? 255 : b;
    // `>> q` convertit en entier (troncature) → godet 0..63 par canal.
    const key = ((((ri >> q) << 12) | ((gi >> q) << 6) | (bi >> q)) as number) >>> 0;
    let idx = cache.get(key);
    if (idx === undefined) {
      const pixel: RgbColor = { r: ri, g: gi, b: bi };
      idx = allowed
        ? findNearestIndexAmong(pixel, palette, allowed)
        : findNearestIndex(pixel, palette);
      cache.set(key, idx);
    }
    return idx;
  };
}
